// The thread composer: typing indicator, reply preview, staged-file chips and
// the input row (attach · text · emoji · send/voice).
//
// Shared by DoctorChatInterface and PatientChatInterface, and a match for the
// MOC patient web app's components/chat/ChatComposer.jsx. MODULE level, like
// every other piece of the thread — see ChatMessageBubble for why. The text
// value stays owned by the interface so the socket typing events and the
// optimistic send read one source of truth.
//
// `lockedReason` (P26): when set, nothing can be written — box, paperclip,
// emoji, send and microphone are all disabled — and the reason is shown above
// them. Used for a read-only (family share) profile. Whatever text is in the
// box stays visible, so a refused line can still be copied.

import React, { useRef } from 'react';
import { File as FileIcon, Image as ImageIcon, Loader2, Lock, Paperclip, Reply, Send, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import EmojiPicker from './EmojiPicker';
import VoiceRecorder from './VoiceRecorder';
import { CHAT_FILE_ACCEPT, MAX_CHAT_FILES } from './chatShared';

const ChatComposer = ({
    value,
    onChange,
    onSend,
    onInsertEmoji,
    error,
    maxLength,
    disabled,
    placeholder,
    peerName,
    peerTyping,
    replyTo,
    replyToLabel,
    onCancelReply,
    files,
    onPickFiles,
    onRemoveFile,
    isUploading,
    onVoiceNote,
    testPrefix,
    lockedReason,
}) => {
    const { t } = useTranslation(['chat']);
    const fileInputRef = useRef(null);
    const textareaRef = useRef(null);
    const staged = files || [];
    const locked = Boolean(lockedReason);
    const canSend = Boolean((value.trim() || staged.length > 0) && !disabled && !isUploading && !locked);

    const insertEmoji = (emoji) => {
        onInsertEmoji(emoji);
        textareaRef.current?.focus();
    };

    return (
        <div className="p-3 border-t border-gray-200 bg-white" data-testid={`${testPrefix}-input-area`}>
            {peerTyping && (
                <div className="mb-2 px-2 py-1.5 flex items-center gap-2 text-sm text-gray-600" data-testid={`${testPrefix}-typing-indicator`}>
                    <span className="flex gap-1">
                        <span className="w-2 h-2 bg-[#4ABDBD] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-[#4ABDBD] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-[#4ABDBD] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                    <span className="font-medium" data-testid={`${testPrefix}-typing-name`}>{peerName}</span>
                    <span className="text-gray-500" data-testid={`${testPrefix}-typing-text`}>{t('chat.isTyping', 'is typing...')}</span>
                </div>
            )}

            {replyTo && (
                <div className="mb-2 px-3 py-2 bg-gray-50 rounded-xl border border-gray-200 flex items-start justify-between" data-testid={`${testPrefix}-reply-preview-container`}>
                    <div className="flex-1 min-w-0 pr-3">
                        <div className="flex items-center gap-1 text-xs font-semibold text-[#4ABDBD] mb-0.5" data-testid={`${testPrefix}-reply-preview-label`}>
                            <Reply size={12} /> {t('chat.replyingTo', 'Replying to')} {replyToLabel}
                        </div>
                        <p className="text-sm text-gray-600 truncate" data-testid={`${testPrefix}-reply-preview-content`}>
                            {replyTo.content || t('chat.attachment', 'Attachment')}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onCancelReply}
                        className="text-gray-400 hover:text-red-500 transition-colors p-1 cursor-pointer"
                        title={t('chat.cancelReply', 'Cancel reply')}
                        aria-label={t('chat.cancelReply', 'Cancel reply')}
                        data-testid={`${testPrefix}-reply-preview-close-button`}
                    >
                        <X size={15} />
                    </button>
                </div>
            )}

            {staged.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2" data-testid={`${testPrefix}-file-previews-container`}>
                    {staged.map((file, index) => (
                        <div
                            key={`${file.name}-${index}`}
                            className="relative bg-gray-50 border border-gray-200 rounded-xl p-2 pr-8 flex items-center gap-2 shadow-sm"
                            data-testid={`${testPrefix}-file-preview-${index}`}
                        >
                            <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${file.type.startsWith('image/') ? 'bg-[#4ABDBD]/10' : 'bg-gray-200'}`}>
                                {file.type.startsWith('image/')
                                    ? <ImageIcon size={15} className="text-[#4ABDBD]" />
                                    : <FileIcon size={15} className="text-gray-500" />}
                            </span>
                            <span className="text-[11px] font-bold text-gray-700 truncate max-w-[110px]" data-testid={`${testPrefix}-file-preview-name-${index}`}>{file.name}</span>
                            <button
                                type="button"
                                onClick={() => onRemoveFile(index)}
                                className="absolute right-1 text-gray-400 hover:text-red-500 p-1 transition-colors cursor-pointer"
                                aria-label={t('chat.removeAttachment', 'Remove attachment')}
                                data-testid={`${testPrefix}-file-preview-remove-button-${index}`}
                            >
                                <X size={13} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {locked && (
                <div
                    role="status"
                    className="mb-2 px-3 py-2 rounded-xl border border-[#C7D6D5] bg-[#F4FAF9] flex items-start gap-2 text-sm text-[#2D8A8A]"
                    data-testid={`${testPrefix}-composer-locked`}
                >
                    <Lock size={15} className="mt-0.5 flex-shrink-0" />
                    <span>{lockedReason}</span>
                </div>
            )}

            {error && <p className="text-xs text-red-500 mb-1.5 px-1" data-testid={`${testPrefix}-input-error`}>{error}</p>}

            <div className="flex items-end gap-2" data-testid={`${testPrefix}-input-row`}>
                <input
                    type="file"
                    ref={fileInputRef}
                    multiple
                    className="hidden"
                    accept={CHAT_FILE_ACCEPT}
                    onChange={(e) => { onPickFiles(e.target.files); e.target.value = ''; }}
                    data-testid={`${testPrefix}-file-input`}
                />
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || locked || isUploading || staged.length >= MAX_CHAT_FILES}
                    className="p-2.5 rounded-xl border border-gray-200 bg-white text-gray-400 hover:text-[#4ABDBD] hover:bg-[#4ABDBD]/5 transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer flex-shrink-0"
                    title={t('chat.attach_files', 'Attach Files')}
                    aria-label={t('chat.attach_files', 'Attach Files')}
                    data-testid={`${testPrefix}-attach-button`}
                >
                    <Paperclip size={18} />
                </button>

                <div className="flex-1 relative" data-testid={`${testPrefix}-input-wrapper`}>
                    <textarea
                        ref={textareaRef}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (!locked) onSend(); }
                        }}
                        rows={1}
                        maxLength={maxLength}
                        placeholder={placeholder}
                        disabled={isUploading || locked}
                        className={`w-full resize-none px-4 py-3 pr-11 text-sm border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40 focus:border-[#4ABDBD] transition-all disabled:bg-gray-50 ${error ? 'border-red-300' : 'border-gray-200'}`}
                        data-testid={`${testPrefix}-message-input`}
                    />
                    {!locked && (
                        <div className="absolute right-1.5 bottom-1.5" data-testid={`${testPrefix}-emoji-picker-wrapper`}>
                            <EmojiPicker onEmojiSelect={insertEmoji} position="top" />
                        </div>
                    )}
                </div>

                {/* Send and record share one slot: the mic only appears while
                    there is nothing staged to send. */}
                {value.trim() || staged.length > 0 ? (
                    <button
                        type="button"
                        onClick={onSend}
                        disabled={!canSend}
                        className="h-11 w-11 rounded-full flex items-center justify-center bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white shadow-sm hover:shadow-md transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer flex-shrink-0"
                        title={t('chat.send', 'Send')}
                        aria-label={t('chat.send', 'Send')}
                        data-testid={`${testPrefix}-send-button`}
                    >
                        {isUploading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    </button>
                ) : (
                    <VoiceRecorder
                        disabled={disabled || locked}
                        isUploading={isUploading}
                        onRecordingComplete={onVoiceNote}
                    />
                )}
            </div>
        </div>
    );
};

export default ChatComposer;
